import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Target, TrendingUp, Wallet, CheckCircle2, Clock, AlertCircle, Info,
} from "lucide-react";

/**
 * Promoter "My Earnings" — mobile-first self-view.
 *
 * Scope: visible only to sales_promoter via the route guard in App.js.
 * Data: GET /promoter/calc/me — server returns the calc engine result for the
 * logged-in promoter ONLY. No filters for other promoters/dealers, no exports,
 * no admin/PM controls.
 *
 * Renders monthly target, approved sales value/count, achievement %, raw +
 * payable incentive, pending sales count/value, and a no-rule warning if any.
 * Falls back to a clean empty state on missing rules / no data.
 */

const fmtNPR = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function StatTile({ icon: Icon, label, value, sub, testId, tone = "default" }) {
  const toneCls = {
    default: "bg-white",
    accent: "bg-slate-900 text-white",
    warn: "bg-amber-50 border-amber-200",
    success: "bg-emerald-50 border-emerald-200",
  }[tone];
  const labelCls = tone === "accent" ? "text-slate-300" : "text-slate-500";
  const subCls = tone === "accent" ? "text-slate-300" : "text-slate-500";
  const iconCls = tone === "accent" ? "text-slate-200" : "text-slate-400";
  return (
    <Card className={`${toneCls} border`}>
      <CardContent className="p-3" data-testid={testId}>
        <div className="flex items-center gap-2 mb-1">
          <Icon size={14} className={iconCls} />
          <span className={`text-[11px] uppercase font-semibold tracking-wide ${labelCls}`}>{label}</span>
        </div>
        <div className="text-lg font-bold leading-tight">{value}</div>
        {sub ? <div className={`text-[11px] mt-0.5 ${subCls}`}>{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

export default function PromoterMyEarnings() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(today);
  const [calc, setCalc] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get("/promoter/calc/me", { params: { month } }),
      api.get("/promoter/calc/me/trend", { params: { months: 6, ref_month: month } }).catch(() => ({ data: null })),
    ])
      .then(([r, t]) => { if (mounted) { setCalc(r.data); setTrend(t.data); } })
      .catch((e) => {
        if (!mounted) return;
        setCalc(null);
        setError(formatApiErrorDetail(e.response?.data?.detail) || null);
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [month]);

  const hasData = !!calc;
  const targetAmount = calc?.target?.sales_amount || 0;
  const approvedValue = calc?.approved_sales_value || 0;          // NET
  const approvedGrossValue = calc?.approved_sales_gross_value || 0;
  const approvedVatValue = calc?.approved_vat_value || 0;
  const approvedCount = calc?.approved_count || 0;
  const achievement = calc?.achievement_pct || 0;
  const progressPct = Math.min(Math.max(achievement, 0), 100);
  const rawIncentive = calc?.raw_incentive || 0;
  const slabPayout = calc?.slab_payout || 0;
  const bonusAmount = calc?.bonus_amount || 0;
  const payableIncentive = calc?.payable_incentive || 0;
  const pendingCount = calc?.pending_count || 0;
  const pendingValue = calc?.pending_value || 0;                  // NET
  const noRuleValue = calc?.no_rule_value || 0;
  const slabName = calc?.slab?.name || calc?.slab?.label || null;
  const hasTarget = !!calc?.target;
  const hasRule = (calc?.rules_used || calc?.rule_count || 0) > 0
    || rawIncentive > 0
    || (calc?.matched_sales_count || 0) > 0;

  return (
    <div className="space-y-4 pb-6" data-testid="promoter-my-earnings">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Earnings</h1>
        <p className="text-xs text-slate-500 mt-1">
          Only your approved sales count. Pending sales are shown separately.
        </p>
      </header>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label className="text-[11px] uppercase font-semibold text-slate-600">Month</Label>
          <Input
            type="month"
            value={month}
            max={today}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9"
            data-testid="my-earnings-month"
          />
        </div>
      </div>

      {loading && (
        <Card><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="my-earnings-loading">
          Loading your earnings…
        </CardContent></Card>
      )}

      {!loading && !hasData && (
        <Card>
          <CardContent className="p-6 text-center" data-testid="my-earnings-empty">
            <Info size={20} className="mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-semibold text-slate-800">No earnings data yet</div>
            <div className="text-xs text-slate-500 mt-1">
              {error
                ? "We couldn't load your earnings right now. Please try again later."
                : "Once your sales are approved and incentive rules are active, your earnings will appear here."}
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && hasData && (
        <>
          {!hasRule && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-3 flex items-start gap-2" data-testid="my-earnings-no-rule">
                <AlertCircle size={16} className="text-amber-600 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <div className="font-semibold">No active incentive rule</div>
                  <div>Your sales won&apos;t earn incentives until your manager activates a rule for this month.</div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-3">
            <StatTile
              icon={Target}
              label="Monthly Target"
              value={hasTarget ? `NPR ${fmtNPR(targetAmount)}` : "—"}
              sub={hasTarget ? null : "Not set"}
              testId="tile-target"
            />
            <StatTile
              icon={CheckCircle2}
              label="Approved Net Sales"
              value={`NPR ${fmtNPR(approvedValue)}`}
              sub={`${approvedCount} sale${approvedCount === 1 ? "" : "s"} · excl. VAT`}
              testId="tile-approved"
              tone="success"
            />
          </div>

          {(approvedGrossValue > 0 || approvedVatValue > 0) && (
            <Card>
              <CardContent className="p-3 text-xs space-y-0.5" data-testid="tile-vat-breakdown">
                <div className="flex items-center gap-2 mb-1">
                  <Info size={12} className="text-slate-400" />
                  <span className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">
                    Invoice breakdown (this month)
                  </span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Gross invoiced</span>
                  <span className="font-mono">NPR {fmtNPR(approvedGrossValue)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>VAT collected</span>
                  <span className="font-mono">NPR {fmtNPR(approvedVatValue)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200 mt-0.5">
                  <span>Counted toward target</span>
                  <span className="font-mono">NPR {fmtNPR(approvedValue)}</span>
                </div>
                <div className="text-[10px] text-slate-500 pt-0.5">
                  Only net sales (excl. VAT) count toward target & incentive achievement.
                </div>
              </CardContent>
            </Card>
          )}

          {hasTarget && (
            <Card>
              <CardContent className="p-4" data-testid="tile-achievement">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={14} className="text-slate-500" />
                    <span className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">
                      Achievement
                    </span>
                  </div>
                  <Badge variant="outline" className="text-xs">{achievement.toFixed(1)}%</Badge>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 bg-slate-900 transition-all"
                    style={{ width: `${progressPct}%` }}
                    data-testid="achievement-bar"
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">
                  NPR {fmtNPR(approvedValue)} of NPR {fmtNPR(targetAmount)}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-slate-900 text-white">
            <CardContent className="p-4" data-testid="tile-payable">
              <div className="flex items-center gap-2 mb-1">
                <Wallet size={14} className="text-slate-200" />
                <span className="text-[11px] uppercase font-semibold tracking-wide text-slate-300">
                  Payable Incentive (this month)
                </span>
              </div>
              <div className="text-3xl font-bold leading-tight">NPR {fmtNPR(payableIncentive)}</div>
              {slabName ? (
                <div className="text-[11px] text-slate-300 mt-1">Slab: {slabName}</div>
              ) : null}
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <StatTile
              icon={Wallet}
              label="Raw Incentive"
              value={`NPR ${fmtNPR(rawIncentive)}`}
              sub="Before slab"
              testId="tile-raw"
            />
            <StatTile
              icon={Wallet}
              label="Slab Payout"
              value={`NPR ${fmtNPR(slabPayout)}`}
              sub={slabName ? `via ${slabName}` : "—"}
              testId="tile-slab"
            />
          </div>

          {bonusAmount > 0 && (
            <StatTile
              icon={Wallet}
              label="Bonus"
              value={`NPR ${fmtNPR(bonusAmount)}`}
              testId="tile-bonus"
              tone="success"
            />
          )}

          <Card>
            <CardContent className="p-3" data-testid="tile-pending">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={14} className="text-slate-500" />
                <span className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">
                  Pending Sales (Net)
                </span>
              </div>
              <div className="text-base font-semibold">
                {pendingCount} sale{pendingCount === 1 ? "" : "s"} · NPR {fmtNPR(pendingValue)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Awaiting approval — not counted in earnings yet.
              </div>
            </CardContent>
          </Card>

          {noRuleValue > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-3 flex items-start gap-2" data-testid="tile-no-rule-warning">
                <AlertCircle size={16} className="text-amber-600 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <div className="font-semibold">
                    NPR {fmtNPR(noRuleValue)} approved sales had no matching incentive rule
                  </div>
                  <div>These sales count toward your target but didn&apos;t earn incentive this month.</div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="text-[11px] text-slate-400 text-center pt-2" data-testid="my-earnings-footnote">
            Updated live · Promoter: {user?.name}
          </div>

          {trend?.series?.length ? (
            <Card data-testid="my-earnings-trend-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={14} className="text-slate-500" />
                  <span className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">
                    Last {trend.series.length} months
                  </span>
                </div>
                <Sparkline series={trend.series} />
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

/* ---------- Sparkline (achievement % + payable incentive over last 6 months) ---------- */
function Sparkline({ series }) {
  const w = 320, h = 80, pad = 4;
  const ach = series.map((s) => Math.max(0, Number(s.achievement_pct || 0)));
  const inc = series.map((s) => Math.max(0, Number(s.payable_incentive || 0)));
  const achMax = Math.max(100, ...ach);
  const incMax = Math.max(1, ...inc);
  const stepX = (w - pad * 2) / Math.max(series.length - 1, 1);
  const ptsAch = ach.map((v, i) => `${pad + i * stepX},${h - pad - ((v / achMax) * (h - pad * 2))}`).join(" ");
  const ptsInc = inc.map((v, i) => `${pad + i * stepX},${h - pad - ((v / incMax) * (h - pad * 2))}`).join(" ");
  return (
    <div className="space-y-2" data-testid="my-earnings-sparkline">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
        <polyline fill="none" stroke="#0f172a" strokeWidth="2" points={ptsAch} />
        <polyline fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="3 3" points={ptsInc} />
        {ach.map((v, i) => (
          <circle key={`a${i}`} cx={pad + i * stepX} cy={h - pad - ((v / achMax) * (h - pad * 2))} r="2" fill="#0f172a" />
        ))}
      </svg>
      <div className="grid grid-cols-6 text-[10px] text-slate-500 text-center">
        {series.map((s) => (
          <div key={s.month} className="truncate">{s.month.slice(5)}<br/>{Number(s.achievement_pct || 0).toFixed(0)}%</div>
        ))}
      </div>
      <div className="flex justify-center gap-4 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-slate-900 inline-block"/> Achievement %</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 inline-block" style={{ borderTop: "1px dashed" }}/> Payable Rs.</span>
      </div>
    </div>
  );
}
